import sodiumModule from "libsodium-wrappers-sumo";

const STORAGE_KEY = "intimsync.web.e2ee.identity_key.v1";

export type WebE2EEIdentityKeyRecord = {
  format: "intimsync.web.e2ee.identity-key";
  version: 1;
  keyId: string;
  algorithm: "x25519-xsalsa20poly1305-box-v1";
  createdAt: string;
  publicKeyBase64: string;
  privateKeyBase64: string;
};

function randomId(bytes: Uint8Array) {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 16);
}

async function sodium() {
  const s: any = sodiumModule as any;
  if (s.ready) await s.ready;
  return s;
}

export function readWebE2EEIdentityKey(): WebE2EEIdentityKeyRecord | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.format !== "intimsync.web.e2ee.identity-key") return null;
    return parsed as WebE2EEIdentityKeyRecord;
  } catch {
    return null;
  }
}

export async function generateWebE2EEIdentityKey(): Promise<WebE2EEIdentityKeyRecord> {
  const s = await sodium();

  if (typeof s.crypto_box_keypair !== "function") {
    throw new Error("web_e2ee_crypto_box_keypair_unavailable");
  }

  const pair = s.crypto_box_keypair();
  const publicKey = pair.publicKey || pair.public_key || pair.pk;
  const privateKey = pair.privateKey || pair.private_key || pair.sk;
  const idBytes = s.randombytes_buf(16);

  if (!publicKey || !privateKey) {
    throw new Error("web_e2ee_invalid_keypair");
  }

  const record: WebE2EEIdentityKeyRecord = {
    format: "intimsync.web.e2ee.identity-key",
    version: 1,
    keyId: `web_identity_${randomId(idBytes)}`,
    algorithm: "x25519-xsalsa20poly1305-box-v1",
    createdAt: new Date().toISOString(),
    publicKeyBase64: s.to_base64(publicKey, s.base64_variants.ORIGINAL),
    privateKeyBase64: s.to_base64(privateKey, s.base64_variants.ORIGINAL),
  };

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  return record;
}

export async function ensureWebE2EEIdentityKey(): Promise<WebE2EEIdentityKeyRecord> {
  return readWebE2EEIdentityKey() || generateWebE2EEIdentityKey();
}

export function forgetWebE2EEIdentityKey() {
  window.localStorage.removeItem(STORAGE_KEY);
}
